import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { LoginForm } from '../components/auth/LoginForm';
import { RegisterForm } from '../components/auth/RegisterForm';

type AuthMode = 'login' | 'register';

export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [mode, setMode] = useState<AuthMode>('login');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const toggleMode = () => {
    setMode(prev => prev === 'login' ? 'register' : 'login');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f1f5f9',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Grid overlay */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(241,245,249,1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(241,245,249,1) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px',
      }} />

      {/* Gold orb left */}
      <div style={{
        position: 'absolute', top: '-10%', left: '-5%',
        width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(148,163,184,0.15) 0%, transparent 70%)',
        filter: 'blur(80px)', pointerEvents: 'none',
      }} />

      {/* Purple orb bottom-right */}
      <div style={{
        position: 'absolute', bottom: '-10%', right: '5%',
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(80,60,180,0.06) 0%, transparent 70%)',
        filter: 'blur(100px)', pointerEvents: 'none',
      }} />

      {/* Left panel — branding (hidden on mobile) */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        padding: '64px 72px',
        borderRight: '1px solid rgba(148,163,184,0.15)',
        position: 'relative', zIndex: 1,
      }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36,
            background: 'linear-gradient(135deg, #c9a55a, #a87a3a)',
            clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#07070f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 600, letterSpacing: '0.3em', color: '#d97706', textTransform: 'uppercase' }}>
            Ledger Pro
          </div>
        </Link>

        {/* Center quote */}
        <div>
          <div style={{
            fontFamily: "'Inter', sans-serif", fontSize: 52, fontWeight: 300,
            lineHeight: 1.05, color: '#f0e8d8', marginBottom: 28,
          }}>
            Where Finance<br />Meets <em style={{
              fontStyle: 'italic',
              background: 'linear-gradient(135deg, #c9a55a, #e8c97a)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>Mastery</em>
          </div>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 300, color: '#5a4a35', lineHeight: 1.8, maxWidth: 400 }}>
            A complete accounting system built for organizations that demand precision and elegance in every transaction.
          </p>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 48, marginTop: 56 }}>
            {[['4,200+', 'Active Companies'], ['1.3M+', 'Invoices Processed'], ['98%', 'Accuracy Rate']].map(([num, label]) => (
              <div key={label}>
                <div style={{
                  fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300,
                  background: 'linear-gradient(135deg, #c9a55a, #e8c97a)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  lineHeight: 1,
                }}>
                  {num}
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#94a3b8', marginTop: 6 }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#2a1a08' }}>
          © 2026 Ledger Pro · All rights reserved
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{
        width: '100%', maxWidth: 520,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '64px 56px',
        position: 'relative', zIndex: 1,
      }}>
        {/* Mobile logo */}
        <Link to="/" style={{ textDecoration: 'none', display: 'none', alignItems: 'center', gap: 10, marginBottom: 40 }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, letterSpacing: '0.3em', color: '#d97706', textTransform: 'uppercase' }}>
            Ledger Pro
          </div>
        </Link>

        {/* Mode tabs */}
        <div style={{
          display: 'flex', marginBottom: 40,
          borderBottom: '1px solid rgba(148,163,184,0.22)',
        }}>
          {(['login', 'register'] as AuthMode[]).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                padding: '0 0 16px',
                marginRight: 32,
                fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500,
                letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer',
                background: 'transparent', border: 'none',
                color: mode === m ? '#d97706' : '#3a2a15',
                borderBottom: mode === m ? '2px solid #c9a55a' : '2px solid transparent',
                marginBottom: -1,
                transition: 'all 0.2s',
              }}
            >
              {m === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>

        {/* Heading */}
        <div style={{ marginBottom: 36 }}>
          <div style={{
            fontFamily: "'Inter', sans-serif", fontSize: 38, fontWeight: 300,
            color: '#f0e8d8', lineHeight: 1.1, marginBottom: 8,
          }}>
            {mode === 'login' ? 'Welcome back' : 'Create account'}
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#4a3828' }}>
            {mode === 'login' ? 'Sign in to your workspace' : 'Set up your organization'}
          </div>
        </div>

        {/* Form container */}
        <div>
          {mode === 'login' ? (
            <LoginForm onToggleMode={toggleMode} />
          ) : (
            <RegisterForm onToggleMode={toggleMode} />
          )}
        </div>

        <div style={{ marginTop: 32, fontFamily: "'Inter', sans-serif", fontSize: 8.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#1e1208', textAlign: 'center' }}>
          © 2026 Ledger Pro
        </div>
      </div>

      {/* Global auth form overrides */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=Cinzel:wght@400;500;600&family=Jost:wght@300;400;500&display=swap');

        .auth-form-wrap input[type="email"],
        .auth-form-wrap input[type="password"],
        .auth-form-wrap input[type="text"] {
          background: rgba(255,255,255,0.03) !important;
          border: 1px solid rgba(148,163,184,0.3) !important;
          border-radius: 3px !important;
          color: #d8c8a8 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 13px !important;
          padding: 12px 16px !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
          outline: none !important;
        }
        .auth-form-wrap input:focus {
          border-color: rgba(217,119,6,0.45) !important;
          box-shadow: 0 0 0 3px rgba(148,163,184,0.15) !important;
          background: rgba(241,245,249,1) !important;
        }
        .auth-form-wrap input::placeholder { color: #3a2a15 !important; }
        .auth-form-wrap label {
          font-family: 'Inter', sans-serif !important;
          font-size: 10px !important;
          font-weight: 500 !important;
          letter-spacing: 0.2em !important;
          text-transform: uppercase !important;
          color: #5a4a35 !important;
        }
        .auth-form-wrap button[type="submit"] {
          background: linear-gradient(135deg, #c9a55a, #b8943a) !important;
          color: #07070f !important;
          border: none !important;
          border-radius: 3px !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          letter-spacing: 0.15em !important;
          text-transform: uppercase !important;
          padding: 14px 32px !important;
          cursor: pointer !important;
          width: 100% !important;
          transition: all 0.2s !important;
        }
        .auth-form-wrap button[type="submit"]:hover {
          background: linear-gradient(135deg, #e8c97a, #c9a55a) !important;
          box-shadow: 0 12px 40px rgba(217,119,6,0.25) !important;
        }
        .auth-form-wrap a {
          color: #c9a55a !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 12px !important;
        }
        .auth-form-wrap p, .auth-form-wrap span {
          font-family: 'Inter', sans-serif !important;
          color: #4a3828 !important;
        }
        .auth-form-wrap .text-red-500, .auth-form-wrap .text-red-600 {
          color: #c96a6a !important;
          font-size: 11px !important;
        }
      `}</style>
    </div>
  );
};
